import { useEffect } from 'modules/core/hooks/useEffect'
import { useRef } from 'react'

interface UsePollingOptions {
  enabled: boolean
  callback: () => void
  intervalMs: number
}

/**
 * A hook that executes a callback at a regular interval when enabled.
 * Does NOT execute on first activation — the caller is responsible for the
 * initial fetch. However, when the interval changes while already enabled,
 * the callback fires immediately so data is never stale for up to one full
 * (potentially long) interval.
 * Automatically stops when enabled becomes false and cleans up on unmount.
 */
export const usePolling = ({ enabled, callback, intervalMs }: UsePollingOptions) => {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  // Track the previous intervalMs so we can detect interval changes while
  // polling is already active (e.g. syncing 2s → idle 3min).
  const prevIntervalRef = useRef<number | null>(null)

  useEffect(() => {
    if (!enabled) {
      prevIntervalRef.current = null
      return
    }

    // If polling was already active (prevIntervalRef is set) and the interval
    // just changed, fire immediately so the caller doesn't wait up to the new
    // (potentially much longer) interval to get fresh data.
    if (prevIntervalRef.current !== null && prevIntervalRef.current !== intervalMs) {
      callbackRef.current()
    }

    prevIntervalRef.current = intervalMs

    const interval = setInterval(() => {
      callbackRef.current()
    }, intervalMs)

    return () => clearInterval(interval)
  }, [enabled, intervalMs])
}
